import React, { useRef, useState, useCallback } from 'react';

const TIMELINES = ['6 months', '1 year', '2 years', '3+ years'];

const ErrorMsg = ({ msg }) => (
  msg ? (
    <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
        <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
        <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      <p className="text-sm" style={{ color: '#EF4444' }}>{msg}</p>
    </div>
  ) : null
);

export default function Step3InvestmentAsk({ formData, errors, onChange, onContinue, onBack, onSetErrors }) {
  const deckRef = useRef(null);
  const [touched, setTouched] = useState({});
  const [shakeBtn, setShakeBtn] = useState(false);
  const [deckError, setDeckError] = useState('');
  const [deckSuccess, setDeckSuccess] = useState(!!formData?.pitchDeckName);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const fundsLen = formData?.useOfFunds?.length || 0;

  const validateField = useCallback((name, value) => {
    switch (name) {
      case 'minInvestment':
        if (!value && value !== 0) return 'Please enter a minimum investment amount';
        if (parseFloat(value) <= 0) return 'Please enter a minimum investment amount';
        return '';
      case 'fundingGoal': {
        if (!value && value !== 0) return 'Funding goal must be greater than or equal to minimum investment';
        if (parseFloat(value) <= 0) return 'Funding goal must be greater than or equal to minimum investment';
        const min = parseFloat(formData?.minInvestment);
        if (!isNaN(min) && parseFloat(value) < min) return 'Funding goal must be greater than or equal to minimum investment';
        return '';
      }
      case 'useOfFunds':
        if (!value?.trim()) return "Please describe how you'll use the funds (minimum 50 characters)";
        if (value?.trim()?.length < 50) return "Please describe how you'll use the funds (minimum 50 characters)";
        return '';
      case 'projectedRoi': {
        if (!value && value !== 0) return 'Please enter a projected ROI percentage between 1% and 1000%';
        const roi = parseFloat(value);
        if (isNaN(roi) || roi < 1 || roi > 1000) return 'Please enter a projected ROI percentage between 1% and 1000%';
        return '';
      }
      case 'investmentTimeline':
        if (!value) return 'Please select an investment timeline';
        return '';
      default:
        return '';
    }
  }, [formData?.minInvestment]);

  const handleBlur = (name, value) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
  };

  const handleChange = (name, value) => {
    onChange({ [name]: value });
    if (touched?.[name]) {
      const err = validateField(name, value);
      onSetErrors(prev => ({ ...prev, [name]: err || undefined }));
    }
    // Re-validate fundingGoal when minInvestment changes
    if (name === 'minInvestment' && touched?.['fundingGoal'] && formData?.fundingGoal) {
      const goalErr = !formData?.fundingGoal ? 'Funding goal must be greater than or equal to minimum investment'
        : parseFloat(formData?.fundingGoal) < parseFloat(value) ? 'Funding goal must be greater than or equal to minimum investment' : '';
      onSetErrors(prev => ({ ...prev, fundingGoal: goalErr || undefined }));
    }
  };

  const simulateUpload = (file) => {
    setUploading(true);
    setUploadProgress(0);
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 25 + 10;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(100);
        setTimeout(() => {
          setUploading(false);
          setDeckSuccess(true);
          onChange({ pitchDeck: file, pitchDeckName: file?.name });
        }, 300);
      } else {
        setUploadProgress(Math.round(progress));
      }
    }, 150);
  };

  const handleDeckFile = (file) => {
    if (!file) return;
    setDeckError('');
    setDeckSuccess(false);
    if (file?.type !== 'application/pdf') {
      setDeckError('Please upload a PDF file');
      onChange({ pitchDeck: null, pitchDeckName: '' });
      if (deckRef?.current) deckRef.current.value = '';
      return;
    }
    if (file?.size > 10 * 1024 * 1024) {
      setDeckError('Pitch deck must be under 10MB');
      onChange({ pitchDeck: null, pitchDeckName: '' });
      if (deckRef?.current) deckRef.current.value = '';
      return;
    }
    simulateUpload(file);
  };

  const clearDeck = (e) => {
    e?.stopPropagation();
    onChange({ pitchDeck: null, pitchDeckName: '' });
    setDeckSuccess(false);
    setDeckError('');
    setUploadProgress(0);
    if (deckRef?.current) deckRef.current.value = '';
  };

  const handleContinueClick = () => {
    const fields = ['minInvestment', 'fundingGoal', 'useOfFunds', 'projectedRoi', 'investmentTimeline'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, formData?.[f] ?? '');
      if (err) newErrors[f] = err;
    });
    if (!formData?.isWomenLed) newErrors.isWomenLed = 'This application is for women-led businesses only';
    setTouched(fields?.reduce((a, f) => ({ ...a, [f]: true }), {}));
    if (Object.keys(newErrors)?.length > 0) {
      onSetErrors(prev => ({ ...prev, ...newErrors }));
      setShakeBtn(true);
      setTimeout(() => setShakeBtn(false), 600);
      const firstErrField = document.querySelector('[data-field-error="true"]');
      if (firstErrField) firstErrField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    onContinue();
  };

  const inputClass = (field) => {
    const hasError = errors?.[field];
    const isValid = touched?.[field] && !hasError && (formData?.[field] || formData?.[field] === 0);
    return `w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
      hasError ? 'border-red-400 bg-red-50' : isValid ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
    }`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
  };

  return (
    <div>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeInUp 0.2s ease-out forwards; }
        @keyframes shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-6px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
        .shake-anim { animation: shake 0.5s ease-in-out; }
      `}</style>
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-1" style={{ color: '#0B1F3B', fontFamily: 'Cormorant Garamond, serif' }}>Investment Details</h2>
        <p className="text-sm" style={{ color: '#6B7280' }}>Step 3 of 4 — Investment Ask</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div data-field-error={!!errors?.minInvestment}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Minimum Investment <span style={{ color: '#D4A520' }}>*</span></label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold" style={{ color: '#D4A520' }}>$</span>
            <input
              type="number"
              value={formData?.minInvestment}
              onChange={(e) => handleChange('minInvestment', e?.target?.value)}
              onBlur={(e) => handleBlur('minInvestment', e?.target?.value)}
              placeholder="5000"
              min="1"
              className={`w-full pl-8 pr-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
                errors?.minInvestment ? 'border-red-400 bg-red-50' : touched?.minInvestment && !errors?.minInvestment && formData?.minInvestment ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
              }`}
              style={{ color: '#0B1F3B' }}
            />
          </div>
          <ErrorMsg msg={errors?.minInvestment} />
        </div>
        <div data-field-error={!!errors?.fundingGoal}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Total Funding Goal <span style={{ color: '#D4A520' }}>*</span></label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold" style={{ color: '#D4A520' }}>$</span>
            <input
              type="number"
              value={formData?.fundingGoal}
              onChange={(e) => handleChange('fundingGoal', e?.target?.value)}
              onBlur={(e) => handleBlur('fundingGoal', e?.target?.value)}
              placeholder="500000"
              min="1"
              className={`w-full pl-8 pr-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 ${
                errors?.fundingGoal ? 'border-red-400 bg-red-50' : touched?.fundingGoal && !errors?.fundingGoal && formData?.fundingGoal ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
              }`}
              style={{ color: '#0B1F3B' }}
            />
          </div>
          <ErrorMsg msg={errors?.fundingGoal} />
        </div>
        <div data-field-error={!!errors?.projectedRoi}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Projected ROI % <span style={{ color: '#D4A520' }}>*</span></label>
          <input
            type="number"
            value={formData?.projectedRoi}
            onChange={(e) => handleChange('projectedRoi', e?.target?.value)}
            onBlur={(e) => handleBlur('projectedRoi', e?.target?.value)}
            placeholder="25"
            min="1"
            max="1000"
            className={inputClass('projectedRoi')}
            style={{ color: '#0B1F3B' }}
          />
          <ErrorMsg msg={errors?.projectedRoi} />
        </div>
        <div data-field-error={!!errors?.investmentTimeline}>
          <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Investment Timeline <span style={{ color: '#D4A520' }}>*</span></label>
          <select
            value={formData?.investmentTimeline}
            onChange={(e) => handleChange('investmentTimeline', e?.target?.value)}
            onBlur={(e) => handleBlur('investmentTimeline', e?.target?.value)}
            className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 appearance-none ${
              errors?.investmentTimeline ? 'border-red-400 bg-red-50' : touched?.investmentTimeline && !errors?.investmentTimeline && formData?.investmentTimeline ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
            }`}
            style={{ color: formData?.investmentTimeline ? '#0B1F3B' : '#9CA3AF' }}
          >
            <option value="">Select timeline...</option>
            {TIMELINES?.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <ErrorMsg msg={errors?.investmentTimeline} />
        </div>
      </div>
      <div className="mb-5" data-field-error={!!errors?.useOfFunds}>
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Use of Funds <span style={{ color: '#D4A520' }}>*</span></label>
        <div className="relative">
          <textarea
            value={formData?.useOfFunds}
            onChange={(e) => handleChange('useOfFunds', e?.target?.value?.slice(0, 300))}
            onBlur={(e) => handleBlur('useOfFunds', e?.target?.value)}
            placeholder="Describe how you will use the investment (e.g., 40% product development, 30% marketing, 30% operations)..."
            rows={4}
            className={`w-full px-4 py-3 rounded-xl border text-sm outline-none transition-all duration-200 resize-none ${
              errors?.useOfFunds ? 'border-red-400 bg-red-50' : touched?.useOfFunds && !errors?.useOfFunds && fundsLen >= 50 ? 'border-[#D4A520]' : 'border-gray-200 focus:border-[#D4A520]'
            }`}
            style={{ color: '#0B1F3B' }}
          />
          <span className={`absolute bottom-3 right-3 text-xs font-medium ${
            fundsLen > 300 ? 'text-red-500' : fundsLen >= 50 ? 'text-[#D4A520]' : 'text-gray-400'
          }`}>{fundsLen}/300</span>
        </div>
        <ErrorMsg msg={errors?.useOfFunds} />
      </div>
      {/* Women-Led Toggle */}
      <div
        className={`flex items-start gap-4 p-4 rounded-xl border-2 mb-5 transition-colors ${
          errors?.isWomenLed ? 'border-amber-400 bg-amber-50' : formData?.isWomenLed ? 'border-[#D4A520] bg-[#D4A520]/5' : 'border-gray-200'
        }`}
        data-field-error={!!errors?.isWomenLed}
      >
        <button
          type="button"
          onClick={() => { onChange({ isWomenLed: !formData?.isWomenLed }); onSetErrors(prev => ({ ...prev, isWomenLed: undefined })); }}
          className="relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 mt-0.5"
          style={{ background: formData?.isWomenLed ? '#D4A520' : '#D1D5DB' }}
          aria-checked={formData?.isWomenLed}
          role="switch"
        >
          <span
            className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
            style={{ transform: formData?.isWomenLed ? 'translateX(24px)' : 'translateX(0)' }}
          />
        </button>
        <div>
          <p className="text-sm font-bold" style={{ color: '#0B1F3B' }}>This is a women-led business <span style={{ color: '#D4A520' }}>*</span></p>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Required — The founder or majority leadership team must identify as women</p>
          {errors?.isWomenLed && (
            <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                <path d="M8 1L1 14h14L8 1z" stroke="#D4A520" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M8 6v4M8 12h.01" stroke="#D4A520" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="text-sm font-medium" style={{ color: '#D4A520' }}>{errors?.isWomenLed}</p>
            </div>
          )}
        </div>
      </div>
      {/* Pitch Deck Upload */}
      <div className="mb-6">
        <label className="block text-sm font-semibold mb-1.5" style={{ color: '#0B1F3B' }}>Pitch Deck <span className="text-gray-400 font-normal">(optional, PDF up to 10MB)</span></label>
        <div
          className={`border-2 border-dashed rounded-xl p-5 cursor-pointer transition-colors ${
            deckError ? 'border-red-400 bg-red-50' : deckSuccess ? 'border-[#D4A520] bg-[#D4A520]/5' : 'border-gray-200 hover:border-[#D4A520] hover:bg-[#D4A520]/5'
          }`}
          onClick={() => !uploading && deckRef?.current?.click()}
        >
          {uploading ? (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F3F4F6' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold" style={{ color: '#0B1F3B' }}>Uploading...</p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>{uploadProgress}%</p>
                </div>
              </div>
              <div className="w-full h-2 rounded-full" style={{ background: '#E5E7EB' }}>
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{ width: `${uploadProgress}%`, background: '#D4A520' }}
                />
              </div>
            </div>
          ) : deckSuccess && formData?.pitchDeckName ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F0FDF4' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold truncate" style={{ color: '#0B1F3B' }}>{formData?.pitchDeckName}</p>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
                    <circle cx="8" cy="8" r="7" fill="#22C55E"/>
                    <path d="M4.5 8l2 2L11 5.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="text-xs" style={{ color: '#6B7280' }}>{formatFileSize(formData?.pitchDeck?.size)} · <span style={{ color: '#D4A520' }}>Click to replace</span></p>
              </div>
              <button
                type="button"
                onClick={clearDeck}
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-red-100 transition-colors"
                style={{ background: '#FEF2F2' }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9 3L3 9M3 3l6 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ) : deckError ? (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#FEF2F2' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
                  <path d="M15 9l-6 6M9 9l6 6" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#EF4444' }}>Upload failed — click to try again</p>
              </div>
              <button
                type="button"
                onClick={clearDeck}
                className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-red-100 transition-colors"
                style={{ background: '#FEF2F2' }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M9 3L3 9M3 3l6 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F3F4F6' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="#D4A520" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: '#0B1F3B' }}>Upload your pitch deck</p>
                <p className="text-xs" style={{ color: '#9CA3AF' }}>PDF format, max 10MB — highly recommended</p>
              </div>
            </div>
          )}
          <input
            ref={deckRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => handleDeckFile(e?.target?.files?.[0])}
          />
        </div>
        {deckError && (
          <div className="flex items-center gap-1 mt-1.5 animate-fadeIn">
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
              <circle cx="8" cy="8" r="7" stroke="#EF4444" strokeWidth="1.5"/>
              <path d="M8 5v3.5M8 11h.01" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <p className="text-sm" style={{ color: '#EF4444' }}>{deckError}</p>
          </div>
        )}
        <p className="text-xs mt-2" style={{ color: '#9CA3AF' }}>No pitch deck? That's okay — you can add one later</p>
      </div>
      {/* Required legend */}
      <p className="text-xs mb-4" style={{ color: '#9CA3AF' }}>* Required fields</p>
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-4 rounded-xl font-bold text-base border-2 transition-all duration-200 hover:bg-gray-50"
          style={{ borderColor: '#0B1F3B', color: '#0B1F3B' }}
        >
          ← Back
        </button>
        <button
          onClick={handleContinueClick}
          className={`flex-1 py-4 rounded-xl font-bold text-base transition-all duration-200 hover:opacity-90 active:scale-[0.98] ${shakeBtn ? 'shake-anim' : ''}`}
          style={{ background: '#D4A520', color: '#0B1F3B' }}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}
